// One recorded stop (cycle_suspensions row) with its PH declaration and RS confirmation
// (an Area Authority confirmation on records made before 0016, labelled historic).
// Used by the Stops & closeout view and by the confirmation panel.
import { SignatureLine } from '@/components/evidence'
import { Badge, KeyValue } from '@/components/ui'
import type { CycleView, SuspensionView } from '@/domain/types'
import { fmtDateTime } from '@/lib/format'
import {
  cycleStateText,
  personText,
  phConfirmationOf,
  reasonLabel,
  reporterCapacityLabel,
  roleLabel,
  rsConfirmationOf,
  suspensionStageText,
  type SuspensionConfirmation,
} from './logic'
import { YesNoBadge } from './parts'

function ConfirmationDetail({ c, safeLabel }: { c: SuspensionConfirmation; safeLabel: string }) {
  return (
    <div className="mt-1 space-y-1 px-1 text-xs text-ink-soft">
      <p className="flex flex-wrap items-center gap-2">
        {safeLabel}: <YesNoBadge value={c.safe_state_confirmed} judge />
      </p>
      {c.notes ? <p className="whitespace-pre-wrap text-ink">Notes: {c.notes}</p> : null}
    </div>
  )
}

export function SuspensionRecord({ s, cycle, showCycle = false }: { s: SuspensionView; cycle: CycleView; showCycle?: boolean }) {
  const stage = suspensionStageText(s, cycle.cycle_state)
  const phConf = phConfirmationOf(s)
  const rsConf = rsConfirmationOf(s)
  const gas = s.source_gas_test_id ? (cycle.gas_tests.find((g) => g.id === s.source_gas_test_id) ?? null) : null
  const reporter = s.reporter ? personText(s.reporter) : s.reporter_capacity === 'system_gas_result' ? 'System (from the gas test record)' : 'Not recorded'

  const items: Array<[string, string]> = [
    ['What was reported', s.reported_condition || '-'],
    ['Reported by', reporter],
    ['Capacity', reporterCapacityLabel(s.reporter_capacity)],
    ['Seen at (as reported)', fmtDateTime(s.reported_observed_at)],
    ['Received by the server', fmtDateTime(s.server_received_at)],
  ]
  if (s.source_gas_test_id) {
    items.push([
      'Source',
      gas
        ? `Unsafe gas test seen at ${fmtDateTime(gas.reported_observed_at)}, recorded by ${gas.attestation.signer.display_name}`
        : 'Unsafe gas test result',
    ])
  }
  if (showCycle) {
    items.push(['Daily cycle', `#${cycle.cycle_number} - now ${cycleStateText(cycle.cycle_state).label}`])
  }

  return (
    <div className="rounded-lg border border-line bg-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="font-semibold text-ink">{reasonLabel(s.reason_code)}</p>
        <Badge tone={stage.tone}>{stage.label}</Badge>
      </div>
      <div className="mt-3">
        <KeyValue items={items} />
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <div className="space-y-2">
          {s.ph_attestation ? <SignatureLine a={s.ph_attestation} label="PH stop report (signed by the PH)" /> : null}
          {phConf ? (
            <div>
              <SignatureLine a={phConf.attestation} label="PH declaration - site left safe" />
              <ConfirmationDetail c={phConf} safeLabel="Site and equipment left safe" />
            </div>
          ) : null}
          {!s.ph_attestation && !phConf ? <SignatureLine a={null} label="PH declaration" /> : null}
        </div>
        <div>
          <SignatureLine a={rsConf?.attestation ?? null} label={rsConf?.confirmation_role === 'AA' ? `${roleLabel('AA')} confirmation` : 'RS confirmation'} />
          {rsConf ? <ConfirmationDetail c={rsConf} safeLabel="Safe state confirmed" /> : null}
        </div>
      </div>
    </div>
  )
}
